import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/common/Badge';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { ConfirmDialog, Modal } from '@/components/common/Modal';
import { ErrorState, LoadingState } from '@/components/common/States';
import { SelectField, TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useAcademicYearPicker, useCampusPicker } from '@/hooks/usePickers';
import { calendarApi } from '@/services/api/endpoints/academic';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { CalendarEvent, CalendarEventType } from '@/types/domain';
import { formatDate, humanise } from '@/utils/format';

const TYPES: CalendarEventType[] = [
  'HOLIDAY',
  'ACADEMIC_EVENT',
  'EXAMINATION',
  'ACTIVITY',
  'MEETING',
  'OTHER',
];

/**
 * Holidays, examinations and events.
 *
 * Attendance reads this calendar, so a holiday recorded once suppresses the
 * register everywhere rather than being remembered per class. Cancelling keeps
 * the row: an event that was announced and then called off is something people
 * ask about, and deleting it would make the calendar disagree with what parents
 * were told.
 */
export function CalendarPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const years = useAcademicYearPicker();
  const campuses = useCampusPicker();

  const [academicYearId, setAcademicYearId] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [cancelling, setCancelling] = useState<CalendarEvent | null>(null);
  const [form, setForm] = useState({
    campusId: '',
    title: '',
    description: '',
    eventType: 'ACADEMIC_EVENT' as CalendarEventType,
    startDate: '',
    endDate: '',
    holiday: false,
  });

  const effectiveYearId = academicYearId === '' ? (years.data?.[0]?.id ?? '') : academicYearId;

  const query = useQuery({
    queryKey: queryKeys.calendar.forYear(effectiveYearId),
    queryFn: () => calendarApi.listForYear(effectiveYearId),
    enabled: effectiveYearId !== '',
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: queryKeys.calendar.all });

  const create = useMutation({
    mutationFn: () =>
      calendarApi.create({
        academicYearId: effectiveYearId,
        campusId: form.campusId === '' ? undefined : form.campusId,
        title: form.title,
        description: form.description === '' ? undefined : form.description,
        eventType: form.eventType,
        startDate: form.startDate,
        // A single-day event is the common case, so an omitted end date means
        // "the same day" rather than being rejected as incomplete.
        endDate: form.endDate === '' ? form.startDate : form.endDate,
        holiday: form.holiday,
      }),
    onSuccess: () => {
      toast.success('Event added to the calendar');
      setFormOpen(false);
      setForm({ ...form, title: '', description: '', startDate: '', endDate: '', holiday: false });
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const cancel = useMutation({
    mutationFn: (id: string) => calendarApi.cancel(id),
    onSuccess: () => {
      toast.success('Event cancelled');
      setCancelling(null);
      invalidate();
    },
    onError: (error) => {
      handleApiError(error);
      setCancelling(null);
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Academic calendar"
        description="Holidays, examinations and events. Attendance reads this to know which days require a register."
        action={
          can(PERMISSIONS.CALENDAR_MANAGE) && effectiveYearId !== '' ? (
            <Button onClick={() => setFormOpen(true)}>Add event</Button>
          ) : undefined
        }
      />

      <Card className="p-5">
        <div className="mb-4 max-w-sm">
          <SelectField
            label="Academic year"
            value={effectiveYearId}
            onChange={(event) => setAcademicYearId(event.target.value)}
          >
            {years.data?.map((year) => (
              <option key={year.id} value={year.id}>
                {year.name}
              </option>
            ))}
          </SelectField>
        </div>

        {query.isPending && effectiveYearId !== '' ? (
          <LoadingState label="Loading calendar" />
        ) : query.error !== null ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : query.data === undefined || query.data.length === 0 ? (
          <p className="py-6 text-center text-sm text-subtle">No events recorded for this year.</p>
        ) : (
          <ul className="divide-y divide-line">
            {query.data.map((event) => (
              <li key={event.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-ink">
                    <span className={event.status === 'CANCELLED' ? 'line-through opacity-60' : ''}>
                      {event.title}
                    </span>
                    {event.holiday && <Badge tone="info">School closed</Badge>}
                    {event.status === 'CANCELLED' && <Badge tone="neutral">Cancelled</Badge>}
                  </p>
                  <p className="mt-0.5 text-xs text-subtle">
                    {formatDate(event.startDate)}
                    {event.endDate !== event.startDate && ` – ${formatDate(event.endDate)}`} ·{' '}
                    {humanise(event.eventType)} ·{' '}
                    {/* School-wide is the meaningful default, so it is stated
                        rather than shown as a blank campus. */}
                    {event.campusName ?? 'All campuses'}
                  </p>
                  {event.description !== undefined && (
                    <p className="mt-1 text-xs text-muted">{event.description}</p>
                  )}
                </div>

                {can(PERMISSIONS.CALENDAR_MANAGE) && event.status === 'ACTIVE' && (
                  <Button size="sm" variant="ghost" onClick={() => setCancelling(event)}>
                    Cancel
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title="Add a calendar event"
        footer={
          <>
            <Button variant="ghost" onClick={() => setFormOpen(false)} disabled={create.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => create.mutate()}
              loading={create.isPending}
              disabled={form.title === '' || form.startDate === ''}
            >
              Add event
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <TextField
            label="Title"
            required
            value={form.title}
            onChange={(event) => setForm({ ...form, title: event.target.value })}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Starts"
              type="date"
              required
              value={form.startDate}
              onChange={(event) => setForm({ ...form, startDate: event.target.value })}
            />
            <TextField
              label="Ends"
              type="date"
              value={form.endDate}
              onChange={(event) => setForm({ ...form, endDate: event.target.value })}
              hint="Leave blank for a single day"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              label="Type"
              required
              value={form.eventType}
              onChange={(event) =>
                setForm({ ...form, eventType: event.target.value as CalendarEventType })
              }
            >
              {TYPES.map((type) => (
                <option key={type} value={type}>
                  {humanise(type)}
                </option>
              ))}
            </SelectField>

            <SelectField
              label="Campus"
              value={form.campusId}
              onChange={(event) => setForm({ ...form, campusId: event.target.value })}
              hint="Leave blank to apply to every campus"
            >
              <option value="">All campuses</option>
              {campuses.data?.map((campus) => (
                <option key={campus.id} value={campus.id}>
                  {campus.name}
                </option>
              ))}
            </SelectField>
          </div>

          <TextField
            label="Description"
            value={form.description}
            onChange={(event) => setForm({ ...form, description: event.target.value })}
          />

          <label className="flex items-start gap-2 text-sm text-ink">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 border-line text-accent focus:ring-accent"              checked={form.holiday}
              onChange={(event) => setForm({ ...form, holiday: event.target.checked })}
            />
            <span>
              The school is closed
              <span className="block text-xs text-subtle">
                Separate from the type — an examination day is not a holiday, and a training day is a
                closure without being one. Attendance reads this box.
              </span>
            </span>
          </label>
        </div>
      </Modal>

      <ConfirmDialog
        open={cancelling !== null}
        title="Cancel this event?"
        message={`${cancelling?.title ?? ''} will be marked cancelled. The record is kept, because it was already announced.`}
        confirmLabel="Cancel event"
        loading={cancel.isPending}
        onCancel={() => setCancelling(null)}
        onConfirm={() => {
          if (cancelling !== null) {
            cancel.mutate(cancelling.id);
          }
        }}
      />
    </div>
  );
}
